import {
  getApiErrorCode,
  getApiErrorMessage,
  isRecord,
  parseApiResponse,
} from '@/shared/utils/apiResponse';
import { request } from '@/shared/utils/requestBuilder';
import { getStoredWorkspaceId } from '@/shared/utils/workspace';
import { relatoriosEmModoApi } from '../config/flags';
import { CONFIG_DOS_PRODUTOS } from '../config/produtos';
import { criarGatewayDemo } from '../demo/gateway';
import {
  type ConfigDoProduto,
  type ProdutoDoRelatorio,
  RELATORIOS_POR_PAGINA,
  type ReportsGateway,
} from '../types';

/** O `code` do envelope do core é o que separa causas sob o mesmo status (dois 409 diferentes). */
export class RelatoriosLexApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    message: string,
    readonly details: unknown,
  ) {
    super(message);
    this.name = 'RelatoriosLexApiError';
  }
}

const ORGANIZACAO_MUDOU = 'A organização mudou. Reabra o relatório.';
const ESPERA_PADRAO_EM_SEGUNDOS = 60;

/**
 * Recusas que pedem outra ação da pessoa, e não "tente de novo": sem acesso, pedidos demais em
 * pouco tempo (com a espera do `Retry-After`) e relatórios pausados no escritório.
 */
const causaDaRecusa = (
  response: Response,
  code: string | null,
): { code: string; message: string } | null => {
  if (response.status === 403)
    return {
      code: 'SEM_ACESSO',
      message:
        'Você não tem acesso aos relatórios deste produto neste escritório. Peça ao administrador do escritório para revisar sua permissão.',
    };
  if (response.status === 429) {
    const espera = Number(response.headers.get('retry-after'));
    const segundos =
      Number.isFinite(espera) && espera > 0 ? Math.ceil(espera) : ESPERA_PADRAO_EM_SEGUNDOS;
    return {
      code: 'MUITOS_PEDIDOS',
      message: `Foram muitos pedidos em pouco tempo. Aguarde ${segundos} segundos e tente de novo.`,
    };
  }
  if (code === 'RELATORIOS_LEX_DESLIGADO')
    return {
      code,
      message:
        'Os relatórios da Lex estão pausados neste escritório no momento. Tente de novo mais tarde.',
    };
  return null;
};

const garantirWorkspace = (workspaceId: string) => {
  if (getStoredWorkspaceId() !== workspaceId) throw new Error(ORGANIZACAO_MUDOU);
};

async function chamar(
  config: ConfigDoProduto,
  workspaceId: string,
  path: string,
  opcoes: {
    method?: 'GET' | 'POST' | 'PUT';
    body?: object;
    params?: Record<string, string | number>;
    idempotencyKey?: string;
  } = {},
): Promise<unknown> {
  garantirWorkspace(workspaceId);
  const response = await request({
    endpoint: `${config.endpoint}${path}`,
    method: opcoes.method ?? 'GET',
    body: opcoes.body,
    params: opcoes.params,
    headers: {
      'X-Workspace-Id': workspaceId,
      ...(opcoes.idempotencyKey ? { 'Idempotency-Key': opcoes.idempotencyKey } : {}),
    },
    recuperarWorkspaceNo403: false,
  });
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    const code = getApiErrorCode(payload);
    const causa = causaDaRecusa(response, code);
    throw new RelatoriosLexApiError(
      response.status,
      causa?.code ?? code,
      causa?.message ?? getApiErrorMessage(payload, `HTTP ${response.status}`),
      isRecord(payload) && isRecord(payload.error) ? payload.error.details : undefined,
    );
  }
  return parseApiResponse<unknown>(response);
}

const segmento = (id: string) => `/${encodeURIComponent(id)}`;

export function criarGatewayHttp(config: ConfigDoProduto): ReportsGateway {
  const { esquemas } = config;
  return {
    async interpret(workspaceId, text, currentBrief) {
      const body = { text, currentBrief };
      return esquemas.plan.parse(
        await chamar(config, workspaceId, '/interpretar', { method: 'POST', body }),
      );
    },
    async list(workspaceId, { cursor, busca, status }) {
      const params = {
        limit: RELATORIOS_POR_PAGINA,
        ...(cursor ? { cursor } : {}),
        ...(busca ? { busca } : {}),
        ...(status?.length ? { status: status.join(',') } : {}),
      };
      return esquemas.paginaDeRelatorios.parse(await chamar(config, workspaceId, '', { params }));
    },
    async readPreferences(workspaceId) {
      return esquemas.preferences.parse(await chamar(config, workspaceId, '/preferencias'));
    },
    async savePreferences(workspaceId, preferences) {
      const body = esquemas.preferences.parse(preferences);
      return esquemas.preferences.parse(
        await chamar(config, workspaceId, '/preferencias', { method: 'PUT', body }),
      );
    },
    async create(workspaceId, brief, idempotencyKey) {
      const body = esquemas.brief.parse(brief);
      return esquemas.job.parse(
        await chamar(config, workspaceId, '', { method: 'POST', body, idempotencyKey }),
      );
    },
    async get(workspaceId, id) {
      return esquemas.job.parse(await chamar(config, workspaceId, segmento(id)));
    },
    async cancel(workspaceId, id) {
      try {
        return esquemas.job.parse(
          await chamar(config, workspaceId, `${segmento(id)}/cancelar`, { method: 'POST' }),
        );
      } catch (error) {
        // A conclusão ganhou a corrida: o 409 traz o estado canônico, que é o que a tela mostra.
        const canonico =
          error instanceof RelatoriosLexApiError && error.status === 409 && isRecord(error.details)
            ? esquemas.job.safeParse(error.details.job)
            : null;
        if (canonico?.success) return canonico.data;
        throw error;
      }
    },
  };
}

/** Escolhido a cada chamada pela flag do produto, nunca no load do módulo. */
export const gatewayDoProduto = (produto: ProdutoDoRelatorio): ReportsGateway => {
  const config = CONFIG_DOS_PRODUTOS[produto];
  return relatoriosEmModoApi(produto) ? criarGatewayHttp(config) : criarGatewayDemo(config);
};

export async function downloadReportPdf(
  config: ConfigDoProduto,
  workspaceId: string,
  id: string,
): Promise<Blob> {
  garantirWorkspace(workspaceId);
  const response = await request({
    endpoint: `${config.endpoint}${segmento(id)}/pdf`,
    method: 'GET',
    responseType: 'blob',
    headers: { 'X-Workspace-Id': workspaceId },
    recuperarWorkspaceNo403: false,
  });
  if (response.status === 409)
    throw new Error('O PDF ainda está sendo preparado. Tente novamente em instantes.');
  if (!response.ok) throw new Error('Não foi possível baixar o PDF. Tente novamente.');
  const blob = await response.blob();
  if (!blob.type.includes('application/pdf'))
    throw new Error('O servidor não retornou um PDF válido.');
  return blob;
}
